import { TFunction } from 'i18next';

export interface NifEndereco {
  cep: string;
  logradouro: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  estado: string;
  uf: string;
}

export interface NifFormData {
  nome: string;
  sobrenome: string;
  dataNascimento: string;
  email: string;
  passaporteNumero: string;
  passaporteValidade: string;
  endereco: NifEndereco;
  consentimentoLGPD: boolean;
}

export type NifWizardStepId =
  | 'personal'
  | 'passport'
  | 'documents'
  | 'address'
  | 'consent'
  | 'review';

export const NIF_WIZARD_STEPS: NifWizardStepId[] = [
  'personal',
  'passport',
  'documents',
  'address',
  'consent',
  'review'
];

export const emptyNifFormData = (): NifFormData => ({
  nome: '',
  sobrenome: '',
  dataNascimento: '',
  email: '',
  passaporteNumero: '',
  passaporteValidade: '',
  endereco: {
    cep: '',
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    estado: '',
    uf: ''
  },
  consentimentoLGPD: false
});

export interface ReviewRow {
  label: string;
  value: string;
}

export interface ReviewSection {
  title: string;
  lines: ReviewRow[];
}

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function validateNifStep(
  step: NifWizardStepId,
  data: NifFormData,
  t: TFunction
): string | null {
  const err = (key: string) => t(`nif.errors.${key}`);

  if (step === 'personal') {
    if (!data.nome.trim() || data.nome.trim().length < 2) return err('nome');
    if (!data.sobrenome.trim() || data.sobrenome.trim().length < 2) return err('sobrenome');
    if (!data.dataNascimento) return err('dataNascimento');
    if (!emailRe.test(data.email.trim())) return err('email');
  }

  if (step === 'passport') {
    if (!data.passaporteNumero.trim()) return err('passaporteNumero');
    if (!data.passaporteValidade) return err('passaporteValidade');
  }

  if (step === 'address') {
    const cep = data.endereco.cep.replace(/\D/g, '');
    if (cep.length !== 8) return err('cep');
    if (!data.endereco.logradouro.trim()) return err('logradouro');
    if (!data.endereco.numero.trim()) return err('numero');
    if (!data.endereco.bairro.trim()) return err('bairro');
    if (!data.endereco.cidade.trim()) return err('cidade');
    if (!data.endereco.estado.trim()) return err('estado');
    if (!data.endereco.uf.trim() || data.endereco.uf.trim().length < 2) return err('uf');
  }

  if (step === 'consent') {
    if (!data.consentimentoLGPD) return err('consent');
  }

  return null;
}

export function buildNifReviewSummary(
  data: NifFormData,
  t: TFunction,
  files: { passport: File | null; residence: File | null }
): ReviewSection[] {
  const L = (key: string) => t(`nif.${key}`);
  const yes = t('common.yes');
  const no = t('common.no');
  const fileLabel = (f: File | null) => (f ? f.name : t('nif.notProvided'));

  const addr = data.endereco;
  const addressLine = [
    addr.logradouro,
    addr.numero,
    addr.complemento,
    addr.bairro,
    `${addr.cidade} - ${addr.uf}`,
    addr.estado,
    addr.cep
  ]
    .filter(Boolean)
    .join(', ');

  return [
    {
      title: L('personal'),
      lines: [
        { label: L('nome'), value: data.nome },
        { label: L('sobrenome'), value: data.sobrenome },
        { label: L('dataNascimento'), value: data.dataNascimento },
        { label: L('email'), value: data.email }
      ]
    },
    {
      title: L('passport'),
      lines: [
        { label: L('passaporteNumero'), value: data.passaporteNumero },
        { label: L('passaporteValidade'), value: data.passaporteValidade }
      ]
    },
    {
      title: L('documents'),
      lines: [
        { label: L('passaporteCopy'), value: fileLabel(files.passport) },
        { label: L('comprovante'), value: fileLabel(files.residence) }
      ]
    },
    {
      title: L('address'),
      lines: [{ label: L('address'), value: addressLine }]
    },
    {
      title: L('consent'),
      lines: [{ label: L('lgpdAccept'), value: data.consentimentoLGPD ? yes : no }]
    }
  ];
}
